import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { formatMoney } from "@/lib/money";
import {
  ArrowDownLeft,
  BarChart3,
  Copy,
  Loader2,
  LogOut,
  QrCode,
  Settings as SettingsIcon,
  Store,
  Wallet as WalletIcon,
} from "lucide-react";
import { toast } from "sonner";

type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
interface DayHours { open: string; close: string; closed: boolean }
type BusinessHours = Record<DayKey, DayHours>;

interface Profile {
  display_name: string;
  cashtag: string;
  is_merchant: boolean;
  store_name: string | null;
  show_cashtag_publicly: boolean;
  business_hours: BusinessHours | null;
}

const DAY_ORDER: { key: DayKey; label: string }[] = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];
const TODAY_KEY: DayKey = (["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as DayKey[])[new Date().getDay()];

const fmtTime = (t: string) => {
  const [h, m] = t.split(":").map(Number);
  if (Number.isNaN(h)) return t;
  const ampm = h >= 12 ? "pm" : "am";
  const hr = ((h + 11) % 12) + 1;
  return `${hr}${m ? `:${String(m).padStart(2, "0")}` : ""}${ampm}`;
};
interface Tx {
  id: string;
  sender_id: string;
  receiver_id: string;
  amount_cents: number;
  note: string | null;
  created_at: string;
}

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const Merchant = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [todayTxs, setTodayTxs] = useState<Tx[]>([]);
  const [senders, setSenders] = useState<Record<string, { display_name: string; cashtag: string }>>({});
  const [loading, setLoading] = useState(true);
  const [pulse, setPulse] = useState(false);

  const loadSenderProfiles = useCallback(async (ids: string[]) => {
    if (!ids.length) return;
    const { data } = await supabase
      .from("profiles")
      .select("user_id, display_name, cashtag")
      .in("user_id", ids);
    setSenders((prev) => {
      const next = { ...prev };
      (data ?? []).forEach((p: any) => {
        next[p.user_id] = { display_name: p.display_name, cashtag: p.cashtag };
      });
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    if (!user) return;
    const [{ data: p }, { data: t }] = await Promise.all([
      supabase
        .from("profiles")
        .select("display_name, cashtag, is_merchant, store_name, show_cashtag_publicly, business_hours")
        .eq("user_id", user.id)
        .maybeSingle(),
      supabase
        .from("transactions")
        .select("*")
        .eq("receiver_id", user.id)
        .gte("created_at", startOfToday().toISOString())
        .order("created_at", { ascending: false }),
    ]);
    setProfile(p as unknown as Profile | null);
    const list = (t as Tx[] | null) ?? [];
    setTodayTxs(list);
    await loadSenderProfiles(Array.from(new Set(list.map((tx) => tx.sender_id))));
    setLoading(false);
  }, [user, loadSenderProfiles]);

  useEffect(() => {
    load();
  }, [load]);

  // Realtime: listen for new incoming transactions
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`merchant-tx-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "transactions",
          filter: `receiver_id=eq.${user.id}`,
        },
        async (payload) => {
          const tx = payload.new as Tx;
          // Only count today's
          if (new Date(tx.created_at) < startOfToday()) return;
          setTodayTxs((prev) => [tx, ...prev]);
          setPulse(true);
          setTimeout(() => setPulse(false), 1200);
          await loadSenderProfiles([tx.sender_id]);
          toast.success(`+${formatMoney(tx.amount_cents)} received 🌴`);
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, loadSenderProfiles]);

  const todayTotal = todayTxs.reduce((s, t) => s + t.amount_cents, 0);
  const payload = profile?.cashtag ? `${window.location.origin}/pay/${profile.cashtag}` : "";

  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out");
    navigate("/", { replace: true });
  };

  const copyTag = async () => {
    if (!profile?.cashtag) return;
    await navigator.clipboard.writeText(`$${profile.cashtag}`);
    toast.success("Cashtag copied");
  };

  return (
    <MobileShell>
      <BrandHeader
        right={
          <div className="flex items-center gap-1">
            <Link
              to="/wallet"
              aria-label="Personal wallet"
              className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-white/10 transition-smooth"
            >
              <WalletIcon className="h-5 w-5" />
            </Link>
            <Link
              to="/settings"
              aria-label="Settings"
              className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-white/10 transition-smooth"
            >
              <SettingsIcon className="h-5 w-5" />
            </Link>
            <button
              onClick={handleSignOut}
              aria-label="Sign out"
              className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-white/10 transition-smooth"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        }
      >
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-xs font-medium">
            <Store className="h-3.5 w-3.5" /> Merchant mode
          </div>
          {profile?.store_name && (
            <p className="mt-3 font-display text-lg font-bold truncate px-6">
              {profile.store_name}
            </p>
          )}
          <p className={`text-xs uppercase tracking-wider opacity-70 ${profile?.store_name ? "mt-1" : "mt-3"}`}>
            Today's earnings
          </p>
          <p
            className={`font-display text-5xl font-extrabold mt-1 transition-transform ${
              pulse ? "scale-110" : "scale-100"
            }`}
          >
            {loading ? "—" : formatMoney(todayTotal)}
          </p>
          <p className="text-xs opacity-80 mt-1">
            {todayTxs.length} {todayTxs.length === 1 ? "payment" : "payments"}
            {profile?.show_cashtag_publicly && profile?.cashtag ? ` · $${profile.cashtag}` : ""}
          </p>
        </div>
      </BrandHeader>

      {/* QR card */}
      <section className="px-5 -mt-5">
        <div className="bg-background rounded-2xl shadow-card p-5 flex items-center gap-4">
          <div className="bg-white border border-border rounded-xl p-2 shrink-0">
            {payload ? (
              <QRCodeSVG value={payload} size={88} level="M" fgColor="hsl(184 70% 24%)" />
            ) : (
              <div className="h-[88px] w-[88px] bg-muted animate-pulse rounded-md" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Show to pay</p>
            {profile?.store_name ? (
              <p className="font-display text-xl font-extrabold text-primary truncate">
                {profile.store_name}
              </p>
            ) : (
              <p className="font-display text-2xl font-extrabold text-primary truncate">
                ${profile?.cashtag ?? "…"}
              </p>
            )}
            {profile?.show_cashtag_publicly ? (
              <button
                onClick={copyTag}
                className="mt-1 text-xs text-primary font-medium inline-flex items-center gap-1"
              >
                <Copy className="h-3.5 w-3.5" /> ${profile?.cashtag ?? ""}
              </button>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">Cashtag hidden</p>
            )}
          </div>
        </div>
      </section>

      {/* Business hours */}
      {profile?.business_hours && (
        <section className="px-5 mt-4">
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display font-bold text-sm flex items-center gap-1.5">
                <span className="text-primary">●</span> Hours today
              </h3>
              <TodayHoursBadge hours={profile.business_hours[TODAY_KEY]} />
            </div>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
              {DAY_ORDER.map(({ key, label }) => {
                const h = profile.business_hours![key];
                const isToday = key === TODAY_KEY;
                return (
                  <li
                    key={key}
                    className={`flex items-center justify-between text-xs py-0.5 ${
                      isToday ? "font-bold text-primary" : "text-muted-foreground"
                    }`}
                  >
                    <span>{label}</span>
                    <span>{h.closed ? "Closed" : `${fmtTime(h.open)}–${fmtTime(h.close)}`}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}

      {/* Actions */}
      <section className="px-5 mt-4 grid grid-cols-2 gap-2">
        <Button asChild variant="hero" size="lg">
          <Link to="/merchant/charge">
            <QrCode className="h-4 w-4 mr-1.5" /> Charge
          </Link>
        </Button>
        <Button asChild variant="outline" size="lg">
          <Link to="/merchant/dashboard">
            <BarChart3 className="h-4 w-4 mr-1.5" /> Earnings
          </Link>
        </Button>
      </section>

      {/* Live feed */}
      <section className="px-5 mt-6 flex-1">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-lg">Live feed</h2>
          <span className="flex items-center gap-1.5 text-xs text-success font-medium">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
            </span>
            Live
          </span>
        </div>

        {loading ? (
          <div className="py-12 grid place-items-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : todayTxs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
            No payments yet today. Show your QR to get paid 🌴
          </div>
        ) : (
          <ul className="space-y-2">
            {todayTxs.map((tx) => {
              const s = senders[tx.sender_id];
              return (
                <li
                  key={tx.id}
                  className="flex items-center gap-3 bg-secondary/50 rounded-xl px-4 py-3 animate-fade-in-up"
                >
                  <div className="h-10 w-10 rounded-full grid place-items-center bg-success/10 text-success">
                    <ArrowDownLeft className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">From {s?.display_name ?? "Customer"}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {s?.cashtag ? `$${s.cashtag} · ` : ""}
                      {new Date(tx.created_at).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                      {tx.note ? ` · "${tx.note}"` : ""}
                    </p>
                  </div>
                  <p className="font-display font-bold text-success">
                    +{formatMoney(tx.amount_cents)}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <div className="safe-bottom" />
    </MobileShell>
  );
};

const TodayHoursBadge = ({ hours }: { hours: DayHours }) => {
  if (hours.closed) {
    return (
      <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
        Closed today
      </span>
    );
  }
  const now = new Date();
  const cur = now.getHours() * 60 + now.getMinutes();
  const [oh, om] = hours.open.split(":").map(Number);
  const [ch, cm] = hours.close.split(":").map(Number);
  const open = oh * 60 + om;
  const close = ch * 60 + cm;
  const isOpen = cur >= open && cur < close;
  return (
    <span
      className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
        isOpen ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"
      }`}
    >
      {isOpen ? `Open · until ${fmtTime(hours.close)}` : `Closed · opens ${fmtTime(hours.open)}`}
    </span>
  );
};

export default Merchant;
